import { AuthBaseClient } from '../base-client';
import type { SearchResponse } from '@vagusx/shared';
import type { CallEvaluationResponse } from '../call-evaluation/types';
import type {
  InteractionResponse,
  InteractionMessageResponse,
  InteractionWithMetadata,
  InteractionListRequest,
  InteractionEventResponse,
  TaskResponse,
  TaskHistoryResponse,
  InteractionSearchRequest,
  CreateInteractionRequest,
  CompleteInteractionRequest,
  UnflagInteractionRequest,
  EscalateInteractionRequest,
  RecordInteractionMessageRequest,
  TaskSearchRequest,
  CreateTaskRequest,
  ChangeTaskStatusRequest,
  AssignTaskRequest,
  AddTaskCommentRequest,
  BulkCompleteTasksRequest,
  TaskSummaryRequest,
  UpdateTaskRequest,
  InteractionChannel,
  TaskStatus,
  TaskInboxSearchRequest,
  TaskWithRelations,
  TaskCountsRequest,
  TaskCountsResponse,
  BulkCompleteResult,
  TaskNote,
  CreateTaskNoteRequest,
  TaskTimelineEvent,
  TaskTimelineResponse,
  TaskRelatedResponse,
  TaskSummaryResponse,
  LiveCallListResponse,
  LiveCallCountResponse,
  LiveCallListenResponse,
} from './types';

/**
 * Client for interaction API endpoints.
 */
export class InteractionClient extends AuthBaseClient {
  /**
   * List interactions with patient and task metadata (Activity view).
   */
  async list(request?: InteractionListRequest): Promise<SearchResponse<InteractionWithMetadata>> {
    const params = new URLSearchParams();
    if (request?.limit) params.set('limit', request.limit.toString());
    if (request?.offset) params.set('offset', request.offset.toString());
    if (request?.channel) params.set('channel', request.channel);
    if (request?.status) params.set('status', request.status);
    if (request?.direction) params.set('direction', request.direction);
    if (request?.outcomeCategory) params.set('outcomeCategory', request.outcomeCategory);
    if (request?.dialed !== undefined) params.set('dialed', request.dialed.toString());
    if (request?.hasTask !== undefined) params.set('hasTask', request.hasTask.toString());
    if (request?.assignedTo) params.set('assignedTo', request.assignedTo);
    if (request?.startDate) params.set('startDate', request.startDate);
    if (request?.endDate) params.set('endDate', request.endDate);
    if (request?.search) params.set('search', request.search);

    const query = params.toString();
    const url = `/api/interactions/list${query ? `?${query}` : ''}`;
    return this.get<SearchResponse<InteractionWithMetadata>>(url);
  }

  /**
   * Every voice call on the line right now, for the Live Calls page.
   *
   * Includes calls that are still ringing — the response's `connected` flag
   * is what separates those from calls someone has actually picked up.
   */
  async listLive(): Promise<LiveCallListResponse> {
    return this.get<LiveCallListResponse>('/api/interactions/live');
  }

  /**
   * How many voice calls are on the line right now, without the roster.
   * Cheap enough to poll from a page that only shows the number.
   */
  async countLive(): Promise<LiveCallCountResponse> {
    return this.get<LiveCallCountResponse>('/api/interactions/live/count');
  }

  /**
   * Ask for permission to listen to a call that is in progress.
   *
   * POST rather than GET: it is not cacheable, and it writes an audit entry —
   * hearing live patient audio is an access event the practice must be able
   * to account for afterwards.
   */
  async listenToCall(interactionId: string): Promise<LiveCallListenResponse> {
    return this.post<LiveCallListenResponse>(`/api/interactions/${interactionId}/listen`, {});
  }

  /**
   * Search interactions (inbox view).
   */
  async search(request: InteractionSearchRequest): Promise<SearchResponse<InteractionResponse>> {
    return this.post<SearchResponse<InteractionResponse>>('/api/interactions/search', request);
  }

  /**
   * Get the post-call LLM evaluation for an interaction (null if not yet
   * evaluated). `improvements` is present only for system admins.
   */
  async getEvaluation(interactionId: string): Promise<CallEvaluationResponse | null> {
    return this.get<CallEvaluationResponse | null>(`/api/interactions/${interactionId}/evaluation`);
  }

  /**
   * Get patient timeline (all interactions for a patient).
   */
  async getPatientTimeline(
    patientId: string,
    options?: {
      limit?: number;
      offset?: number;
      channel?: InteractionChannel;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<SearchResponse<InteractionResponse>> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.channel) params.set('channel', options.channel);
    if (options?.startDate) params.set('startDate', options.startDate);
    if (options?.endDate) params.set('endDate', options.endDate);

    const query = params.toString();
    const url = `/api/interactions/patient/${patientId}${query ? `?${query}` : ''}`;
    return this.get<SearchResponse<InteractionResponse>>(url);
  }

  /**
   * Get interaction by ID.
   */
  async getById(id: string): Promise<InteractionResponse> {
    return this.get<InteractionResponse>(`/api/interactions/${id}`);
  }

  /**
   * Create a new interaction.
   */
  async create(data: CreateInteractionRequest): Promise<InteractionResponse> {
    return this.post<InteractionResponse>('/api/interactions', data);
  }

  /**
   * Start an interaction.
   */
  async start(id: string): Promise<InteractionResponse> {
    return this.post<InteractionResponse>(`/api/interactions/${id}/start`, {});
  }

  /**
   * Complete an interaction.
   */
  async complete(id: string, data?: CompleteInteractionRequest): Promise<InteractionResponse> {
    return this.post<InteractionResponse>(`/api/interactions/${id}/complete`, data ?? {});
  }

  /**
   * Mark interaction as failed.
   */
  async fail(id: string): Promise<InteractionResponse> {
    return this.post<InteractionResponse>(`/api/interactions/${id}/fail`, {});
  }

  /**
   * Escalate interaction to human.
   */
  async escalate(id: string, data: EscalateInteractionRequest): Promise<InteractionResponse> {
    return this.post<InteractionResponse>(`/api/interactions/${id}/escalate`, data);
  }

  /**
   * Get messages for an interaction.
   */
  async getMessages(interactionId: string): Promise<InteractionMessageResponse[]> {
    return this.get<InteractionMessageResponse[]>(`/api/interactions/${interactionId}/messages`);
  }

  /**
   * Get timeline events for an interaction.
   */
  async getTimeline(interactionId: string): Promise<InteractionEventResponse[]> {
    return this.get<InteractionEventResponse[]>(`/api/interactions/${interactionId}/timeline`);
  }

  /**
   * Record a message or tool event on an interaction.
   * Kind-discriminated body — see RecordInteractionMessageRequest.
   */
  async recordMessage(
    interactionId: string,
    data: RecordInteractionMessageRequest
  ): Promise<InteractionMessageResponse> {
    return this.post<InteractionMessageResponse>(
      `/api/interactions/${interactionId}/messages`,
      data
    );
  }

  /**
   * Flag an interaction (also auto-flags the patient).
   */
  async flagInteraction(id: string): Promise<InteractionResponse> {
    return this.post<InteractionResponse>(`/api/interactions/${id}/flag`, {});
  }

  /**
   * Unflag an interaction.
   * Optionally pass unflagPatient: true to also unflag the patient.
   */
  async unflagInteraction(
    id: string,
    options?: UnflagInteractionRequest
  ): Promise<InteractionResponse> {
    return this.post<InteractionResponse>(`/api/interactions/${id}/unflag`, options ?? {});
  }

  /**
   * Mint a short-TTL signed URL for the recording on this interaction.
   * Throws (HttpError 404) if no recording is on file yet.
   * The returned URL expires in ~30 minutes — call this each play.
   */
  async getRecordingUrl(id: string): Promise<{ url: string; expiresAt: string }> {
    return this.get<{ url: string; expiresAt: string }>(`/api/interactions/${id}/recording-url`);
  }
}

/**
 * Client for task API endpoints.
 */
export class TaskClient extends AuthBaseClient {
  /**
   * Search tasks (task inbox).
   */
  async search(request: TaskSearchRequest): Promise<SearchResponse<TaskResponse>> {
    return this.post<SearchResponse<TaskResponse>>('/api/tasks/search', request);
  }

  /**
   * Get tasks for an interaction.
   */
  async getByInteractionId(interactionId: string): Promise<TaskResponse[]> {
    return this.get<TaskResponse[]>(`/api/tasks/interaction/${interactionId}`);
  }

  /**
   * Get tasks for a patient.
   */
  async getByPatientId(
    patientId: string,
    options?: {
      limit?: number;
      offset?: number;
      status?: TaskStatus;
    }
  ): Promise<SearchResponse<TaskResponse>> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.status) params.set('status', options.status);

    const query = params.toString();
    const url = `/api/tasks/patient/${patientId}${query ? `?${query}` : ''}`;
    return this.get<SearchResponse<TaskResponse>>(url);
  }

  /**
   * Get task by ID.
   */
  async getById(id: string): Promise<TaskResponse> {
    return this.get<TaskResponse>(`/api/tasks/${id}`);
  }

  /**
   * Create a new task.
   */
  async create(data: CreateTaskRequest): Promise<TaskResponse> {
    return this.post<TaskResponse>('/api/tasks', data);
  }

  /**
   * Update a task.
   */
  async update(id: string, data: UpdateTaskRequest): Promise<TaskResponse> {
    return this.patch<TaskResponse>(`/api/tasks/${id}`, data);
  }

  /**
   * Change task status.
   */
  async changeStatus(id: string, status: TaskStatus): Promise<TaskResponse> {
    const body: ChangeTaskStatusRequest = { status };
    return this.post<TaskResponse>(`/api/tasks/${id}/status`, body);
  }

  /**
   * Bulk-mark a set of tasks as completed. Per-row best-effort —
   * one bad id (not in org, archived, etc.) doesn't block the others.
   * Response reports per-row success/failure so the UI can show
   * partial-success messaging without an extra round-trip. Cap is
   * 100 ids per request, enforced server-side via zod.
   */
  async bulkComplete(ids: string[]): Promise<BulkCompleteResult> {
    const body: BulkCompleteTasksRequest = { ids };
    return this.post<BulkCompleteResult>('/api/tasks/bulk-complete', body);
  }

  /**
   * Assign task to a user or team.
   */
  async assign(
    id: string,
    assigneeType: AssignTaskRequest['assigneeType'],
    assigneeId: string
  ): Promise<TaskResponse> {
    const body: AssignTaskRequest = { assigneeType, assigneeId };
    return this.post<TaskResponse>(`/api/tasks/${id}/assign`, body);
  }

  /**
   * Unassign task.
   */
  async unassign(id: string): Promise<TaskResponse> {
    return this.post<TaskResponse>(`/api/tasks/${id}/unassign`, {});
  }

  /**
   * Mark task for EHR sync.
   */
  async markForEhrSync(id: string): Promise<TaskResponse> {
    return this.post<TaskResponse>(`/api/tasks/${id}/sync`, {});
  }

  /**
   * Get task history.
   */
  async getHistory(taskId: string): Promise<TaskHistoryResponse[]> {
    return this.get<TaskHistoryResponse[]>(`/api/tasks/${taskId}/history`);
  }

  /**
   * Add comment to task.
   */
  async addComment(taskId: string, comment: string): Promise<TaskHistoryResponse> {
    const body: AddTaskCommentRequest = { comment };
    return this.post<TaskHistoryResponse>(`/api/tasks/${taskId}/comments`, body);
  }

  /**
   * Search tasks (enriched with email/referral data).
   */
  async searchInbox(request: TaskInboxSearchRequest): Promise<SearchResponse<TaskWithRelations>> {
    return this.post<SearchResponse<TaskWithRelations>>('/api/tasks/search', request);
  }

  /**
   * Aggregate task counts for the inbox header (per-scope tabs +
   * per-label pinned chips within the active scope).
   */
  async getInboxCounts(request: TaskCountsRequest): Promise<TaskCountsResponse> {
    return this.post<TaskCountsResponse>('/api/tasks/counts', request);
  }

  /**
   * Mark task as read.
   */
  async markRead(id: string): Promise<TaskResponse> {
    return this.post<TaskResponse>(`/api/tasks/${id}/read`, {});
  }

  /**
   * Mark task as unread.
   */
  async markUnread(id: string): Promise<TaskResponse> {
    return this.post<TaskResponse>(`/api/tasks/${id}/unread`, {});
  }

  /**
   * Mark task as starred.
   */
  async markStarred(id: string): Promise<TaskResponse> {
    return this.post<TaskResponse>(`/api/tasks/${id}/star`, {});
  }

  /**
   * Mark task as unstarred.
   */
  async markUnstarred(id: string): Promise<TaskResponse> {
    return this.post<TaskResponse>(`/api/tasks/${id}/unstar`, {});
  }
}

/**
 * Task overview client — notes, timeline, related, summary.
 */
export class TaskOverviewClient extends AuthBaseClient {
  /** List internal notes attached to a task (newest first). */
  async listNotes(taskId: string): Promise<TaskNote[]> {
    return this.get<TaskNote[]>(`/api/tasks/${taskId}/notes`);
  }

  /** Create an internal note. `mentionedUserIds` is the canonical mention list. */
  async createNote(taskId: string, data: CreateTaskNoteRequest): Promise<TaskNote> {
    return this.post<TaskNote>(`/api/tasks/${taskId}/notes`, data);
  }

  /** Soft-delete an author's own note. */
  async deleteNote(taskId: string, noteId: string): Promise<{ id: string }> {
    return this.delete<{ id: string }>(`/api/tasks/${taskId}/notes/${noteId}`);
  }

  /**
   * Unified timeline (interactions + history + notes), newest-first.
   * Bounded per-source on the server; the response includes
   * `hasMoreByKind` so the FE can surface a "load more" affordance
   * when a source hit its limit. Fetch-more is a follow-up.
   */
  async getTimeline(taskId: string): Promise<TaskTimelineResponse> {
    return this.get<TaskTimelineResponse>(`/api/tasks/${taskId}/timeline`);
  }

  /** Children of this task + other open tasks for the same patient. */
  async getRelated(taskId: string): Promise<TaskRelatedResponse> {
    return this.get<TaskRelatedResponse>(`/api/tasks/${taskId}/related`);
  }

  /**
   * Returns the cached LLM summary or generates one on cache miss.
   * Pass `regenerate: true` to bypass cache and overwrite.
   */
  async getSummary(taskId: string, regenerate = false): Promise<TaskSummaryResponse> {
    const body: TaskSummaryRequest = { regenerate };
    return this.post<TaskSummaryResponse>(`/api/tasks/${taskId}/summary`, body);
  }
}
